import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SiteContent {
  key: string;
  value: Record<string, string>;
  description?: string;
}

// Fetch a single content block by key
export const useSiteContent = (key: string) => {
  return useQuery({
    queryKey: ["site_content", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("store_settings")
        .select("*")
        .eq("key", key)
        .single();
      
      if (error) {
        // Return null if not found, don't throw
        if (error.code === "PGRST116") return null;
        throw error;
      }
      
      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

// Fetch multiple content blocks at once
export const useSiteContents = (keys: string[]) => {
  return useQuery({
    queryKey: ["site_contents", keys],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("store_settings")
        .select("*")
        .in("key", keys);
      
      if (error) throw error;
      
      // Return as a map for easy access
      const contentMap: Record<string, any> = {};
      data?.forEach((item) => {
        contentMap[item.key] = item.value;
      });
      
      return contentMap;
    },
    staleTime: 5 * 60 * 1000,
  });
};

// Helper to get a value with fallback
export const getContentValue = (
  content: Record<string, any> | null | undefined,
  key: string,
  fallback: string
): string => {
  if (!content || typeof content !== "object") return fallback;
  return (content as Record<string, string>)[key] ?? fallback;
};
